import React, { memo } from 'react'

import {
  array, arrayOf, bool, func, node, number, object, oneOf, shape, string,
} from 'prop-types'

import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'

import Filters from './Components/Filters/Filters'
import Gallery from './Components/Gallery/Gallery'
import { DEFAULT_COMMUNITY_GALLERY_FILTERS } from './constants'

// Controlled, host-agnostic gallery panel. The host owns filter state (a Zustand
// store on /ai-3d-model-generator/3d-models, local state elsewhere) and decides what a card click
// does, passing both down as values + `onX` handlers. The host also supplies the
// empty-state node (and, where it wants a "no exact match - showing popular"
// fallback, the notice node + `enableFallback`), so the panel itself stays free
// of any page-specific copy. The panel only fetches and renders. See the
// ThreeDStudio container for the canonical wiring.
function CommunityGalleryPanel({
  searchQuery = DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery,
  onSearchChange = NOOP,
  category = DEFAULT_COMMUNITY_GALLERY_FILTERS.category,
  categories = EMPTY_ARRAY,
  onCategoryChange = NOOP,
  sortOrder = DEFAULT_COMMUNITY_GALLERY_FILTERS.sortOrder,
  initialModels = null,
  paginationMode = 'infinite',
  onCardClick = NOOP,
  onAuthGatedLoadMore = NOOP,
  anonymousPageLimit = null,
  emptyState = null,
  noMatchNotice = null,
  enableFallback = false,
  extractingCoreStyles = false,
  authLoaded = false,
  isUserLoggedIn = false,
  onSignInRequired = NOOP,
}) {
  return (
    <div className="community-gallery-panel">
      <Filters
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        category={category}
        categories={categories}
        onCategoryChange={onCategoryChange}
      />
      <Gallery
        searchQuery={searchQuery}
        category={category}
        sortOrder={sortOrder}
        initialModels={initialModels}
        paginationMode={paginationMode}
        onCardClick={onCardClick}
        onAuthGatedLoadMore={onAuthGatedLoadMore}
        anonymousPageLimit={anonymousPageLimit}
        emptyState={emptyState}
        noMatchNotice={noMatchNotice}
        enableFallback={enableFallback}
        extractingCoreStyles={extractingCoreStyles}
        authLoaded={authLoaded}
        isUserLoggedIn={isUserLoggedIn}
        onSignInRequired={onSignInRequired}
      />
    </div>
  )
}

CommunityGalleryPanel.propTypes = {
  searchQuery: string,
  onSearchChange: func,
  category: string,
  categories: arrayOf(shape({
    name: string.isRequired,
    value: string.isRequired,
  })),
  onCategoryChange: func,
  sortOrder: string,
  initialModels: shape({
    data: array,
    meta: object,
  }),
  paginationMode: oneOf(['infinite', 'loadMore']),
  onCardClick: func,
  onAuthGatedLoadMore: func,
  anonymousPageLimit: number,
  emptyState: node,
  noMatchNotice: node,
  enableFallback: bool,
  extractingCoreStyles: bool,
  authLoaded: bool,
  isUserLoggedIn: bool,
  onSignInRequired: func,
}

export default memo(CommunityGalleryPanel)
